class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Dont pop cart drawer if already on /cart page
    if (window.location.pathname === "/cart" ) return window.location.reload()
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Remove empty state classes
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.classList.remove('is-empty');
    
    this.productId = parsedState.id;
    
    // Check if sections data exists
    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionHtml = parsedState.sections[section.id];
        if (!sectionHtml) return;
        
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        
        if (sectionElement) {
          try {
            sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
          } catch (e) {
            console.error('Error updating section:', section.id, e);
          }
        }
      });
    } else {
      // Fallback: fetch fresh cart drawer content
      this.fetchAndUpdateDrawer();
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  // Helper method to fetch and update drawer content
  async fetchAndUpdateDrawer() {
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      const html = await response.text();
      const newDoc = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = newDoc.querySelector('#CartDrawer');
      const currentDrawer = document.getElementById('CartDrawer');
      if (newDrawer && currentDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
      }
      
      // Update cart icon bubble
      const bubbleResponse = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`);
      const bubbleHtml = await bubbleResponse.text();
      const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const newBubble = bubbleDoc.querySelector('.shopify-section');
      const oldBubble = document.getElementById('cart-icon-bubble');
      if (newBubble && oldBubble) {
        oldBubble.innerHTML = newBubble.innerHTML;
      }
    } catch (err) {
      console.error('fetchAndUpdateDrawer error:', err);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const element = doc.querySelector(selector);
      return element ? element.innerHTML : '';
    } catch (e) {
      console.error('getSectionInnerHTML error:', e);
      return '';
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// CartDrawerItems needs to extend CartItems which is defined in cart.js
// Since both scripts are deferred, we need to wait for cart-items to be defined
function defineCartDrawerItems() {
  if (customElements.get('cart-drawer-items')) return; // Already defined
  
  const BaseCartItems = customElements.get('cart-items');
  
  if (BaseCartItems) {
    class CartDrawerItems extends BaseCartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }
    }
    customElements.define('cart-drawer-items', CartDrawerItems);
  } else {
    // CartItems not yet defined, retry after a short delay
    setTimeout(defineCartDrawerItems, 50);
  }
}

// Start trying to define CartDrawerItems
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', defineCartDrawerItems);
} else {
  defineCartDrawerItems();
}
